import type {
  NewSubscriptionInput,
  Subscription,
  SubscriptionStore,
} from "./types";

const STORAGE_KEY = "proofly.subscriptions.v1";

function readFromStorage(): Subscription[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed as Subscription[];
  } catch {
    return [];
  }
}

function writeToStorage(subs: Subscription[]): void {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(subs));
}

function generateId(): string {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }
  return `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

export function createLocalStorageSubscriptionStore(): SubscriptionStore {
  const listeners = new Set<() => void>();

  // Cached snapshot reference — must be stable between mutations so
  // useSyncExternalStore's Object.is comparison doesn't loop on every render.
  let snapshot: Subscription[] = readFromStorage();

  function emit(): void {
    for (const listener of listeners) {
      listener();
    }
  }

  function commit(next: Subscription[]): void {
    snapshot = next;
    writeToStorage(next);
    emit();
  }

  if (typeof window !== "undefined") {
    window.addEventListener("storage", (event) => {
      if (event.key !== STORAGE_KEY) return;
      snapshot = readFromStorage();
      emit();
    });
  }

  return {
    list(): Subscription[] {
      return snapshot;
    },

    get(id: string): Subscription | undefined {
      return snapshot.find((s) => s.id === id);
    },

    async add(input: NewSubscriptionInput): Promise<Subscription> {
      const now = new Date().toISOString();
      const id = generateId();
      const sub: Subscription = {
        id,
        vendor: input.vendor,
        categoryId: input.categoryId ?? null,
        categoryLabel: input.categoryLabel ?? null,
        cadence: input.cadence ?? null,
        lastChargeCents: input.lastChargeCents ?? null,
        lastChargeOn: input.lastChargeOn ?? null,
        status: input.status ?? "active",
        notes: input.notes ?? null,
        createdAt: now,
        updatedAt: now,
      };
      commit([sub, ...snapshot]);
      return sub;
    },

    async update(
      id: string,
      patch: Partial<Omit<Subscription, "id" | "createdAt" | "updatedAt">>,
    ): Promise<Subscription> {
      const idx = snapshot.findIndex((s) => s.id === id);
      if (idx === -1) throw new Error(`Subscription ${id} not found`);
      const current = snapshot[idx]!;
      const updated: Subscription = {
        ...current,
        ...patch,
        id: current.id,
        createdAt: current.createdAt,
        updatedAt: new Date().toISOString(),
      };
      const next = snapshot.slice();
      next[idx] = updated;
      commit(next);
      return updated;
    },

    async remove(id: string): Promise<void> {
      const next = snapshot.filter((s) => s.id !== id);
      if (next.length !== snapshot.length) commit(next);
    },

    subscribe(listener: () => void): () => void {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}
